import React, { ReactElement, useEffect, useState } from 'react'
import { StyleProp, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'

import { Color, Spacing } from '../../style'
import { Hyperlink } from '../button'
import Select from '../select/single'
import { TablePagination } from '../table'
import { Text } from '../typography'

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  content: {
    marginHorizontal: Spacing.S,
    flexDirection: 'row',
  },
  button: {
    padding: Spacing.XS,
  },
  icon: {
    color: Color.metalGrey,
  },
  count: {
    marginLeft: Spacing.S,
    marginRight: Spacing.M,
    color: Color.metalGrey,
    fontSize: 14,
  },
  size: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: Spacing.S,
    marginRight: Spacing.S,
  },
  sizeLabel: {
    color: Color.metalGrey,
    fontSize: 14,
  },
  sizeOption: {
    paddingLeft: Spacing.XS,
    paddingRight: Spacing.XS,
    backgroundColor: Color.backgroundGrey,
  },
  sizeSelect: {
    borderColor: Color.transparent,
    backgroundColor: Color.transparent,
  },
  sizeSelectContent: {
    borderTopStartRadius: undefined,
    borderTopEndRadius: undefined,
    borderTopWidth: undefined,
    zIndex: 100,
  },
  sizeArrow: {
    marginLeft: Spacing.XXS,
    color: Color.metalGrey,
  },
})

export enum Item {
  Count = 1,
  Size = 2,
  Buttons = 4,
  All = 7,
}

export interface Props extends ViewProps {
  count?: number
  value?: TablePagination
  renderCount?: (shown: string) => string | ReactElement
  sizeLabel?: string
  onChange?: (value: TablePagination) => void
  contentContainerStyle?: StyleProp<ViewStyle>
  items?: Item
  maxSize?: number
}
const Pagination = (props: Props) => {
  const {
    count = 0,
    value,
    renderCount,
    sizeLabel,
    onChange,
    style,
    contentContainerStyle,
    items = Item.All,
    maxSize,
    ...otherProps
  } = props
  const [size, setSize] = useState(value?.size || 10)

  const lastPage = Math.max(0, Math.ceil(count / size) - 1)
  const [page, setPage] = useState(Math.min(lastPage, value?.page || 0))

  const leftDisabled = page === 0
  const rightDisabled = page === lastPage

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(count / size) - 1)
    setPage(Math.min(lastPage, value?.page || 0))
  }, [count, size])

  const itemIsActive = (item: Item) => (items & item) > 0

  const updatePage = (type: 'first' | 'prev' | 'next' | 'last') => () => {
    let newPage = page
    switch (type) {
      case 'first':
        newPage = 0
        break
      case 'prev':
        newPage = Math.max(0, page - 1)
        break
      case 'next':
        newPage = Math.min(lastPage, page + 1)
        break
      case 'last':
        newPage = lastPage
        break
    }
    setPage(newPage)
    onChange && onChange({ page: newPage, size })
  }

  const updateSize = (value: number) => {
    const start = Math.min(count, page * size + 1)
    setSize(value)
    const newPage = Math.floor(start / value)
    setPage(newPage)
    onChange && onChange({ page: newPage, size: value })
  }

  const sizes = () => {
    const allSizes = [10]
    const multiplier = [2, 2.5, 2]

    let newSize = 0
    while (newSize < count && (!maxSize || newSize < maxSize)) {
      newSize = allSizes[allSizes.length - 1] * multiplier[(allSizes.length - 1) % multiplier.length]
      allSizes.push(newSize)
    }

    if (size && allSizes.indexOf(size) < 0) {
      allSizes.push(size)
      allSizes.sort((a, b) => a - b)
    }
    return allSizes
  }

  const renderSize = () => {
    return (
      <View style={styles.size}>
        {sizeLabel && <Text style={styles.sizeLabel}>{sizeLabel}</Text>}
        <Select
          value={size}
          items={sizes().map((size) => ({ value: size }))}
          contentContainerStyle={[styles.sizeSelectContent, contentContainerStyle]}
          contentOutlineStyle={styles.sizeSelect}
          valueStyle={styles.sizeSelect}
          arrowStyle={styles.sizeArrow}
          optionStyle={styles.sizeOption}
          optionTextStyle={styles.sizeLabel}
          onSelect={updateSize}
          testID="Pagination.Size"
        />
      </View>
    )
  }

  const renderCountText = () => {
    const start = Math.min(count, page * size + 1)
    const end = Math.min((page + 1) * size, count)
    const shown = `${start}${start !== end ? `-${end}` : ''}`

    const countRendered = renderCount && renderCount(shown)
    if (countRendered && typeof countRendered !== 'string') return countRendered

    return <Text style={styles.count}>{countRendered || `${shown} of ${count}`}</Text>
  }
  const renderButtons = () => {
    return (
      <>
        <View style={[styles.content, contentContainerStyle]}>
          <Hyperlink
            style={styles.button}
            disabled={leftDisabled}
            onPress={updatePage('first')}
            left="first"
            iconSize={12}
            textStyle={styles.icon}
            testID="Pagination.First"
          />
          <Hyperlink
            style={styles.button}
            disabled={leftDisabled}
            onPress={updatePage('prev')}
            left="left"
            iconSize={12}
            textStyle={styles.icon}
            testID="Pagination.Prev"
          />
        </View>
        <View style={styles.content}>
          <Hyperlink
            style={styles.button}
            disabled={rightDisabled}
            onPress={updatePage('next')}
            left="right"
            iconSize={12}
            textStyle={styles.icon}
            testID="Pagination.Next"
          />
          <Hyperlink
            style={styles.button}
            disabled={rightDisabled}
            onPress={updatePage('last')}
            left="last"
            iconSize={12}
            textStyle={styles.icon}
            testID="Pagination.Last"
          />
        </View>
      </>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {itemIsActive(Item.Size) && renderSize()}
      {itemIsActive(Item.Count) && renderCountText()}
      {itemIsActive(Item.Buttons) && renderButtons()}
    </View>
  )
}

export default Pagination
